import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { KpiCard, PageHeader, Panel, StatusBadge, EmptyState } from "@/components/admin/shared";
import { waLink, waMessage } from "@/lib/constants";
import { LogOut, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function LiveVisitors() {
  const navigate = useNavigate();
  const [live, setLive] = useState(null);

  const load = async () => setLive((await api.get("/dashboard/live")).data);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const exit = async (id) => { await api.post(`/visits/${id}/exit`); toast.success("Marked as exited"); load(); };

  if (!live) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  return (
    <div>
      <PageHeader title="Live Visitors" subtitle="Real-time premises activity" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="live-kpi-inside" label="Currently Inside" value={live.current_inside} accent="#10B981" />
        <KpiCard testid="live-kpi-entries" label="Entries Today" value={live.entries_today} accent="#081226" />
        <KpiCard testid="live-kpi-exits" label="Exits Today" value={live.exits_today} accent="#D95D1E" />
        <KpiCard testid="live-kpi-last" label="Last Visitor" value={live.last_visitor || "—"} sub={live.last_entry_time} accent="#E8752A" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Currently Inside" action={<span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />{live.current_inside}</span>}>
          {live.inside_list.length === 0 ? <EmptyState text="No visitors inside right now" /> : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {live.inside_list.map((v) => (
                <div key={v.visit_id} data-testid="inside-visitor-row" className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/admin/visitors/${v.visitor_id}`)}>
                    <p className="text-sm font-semibold text-[#081226]">{v.full_name}</p>
                    <p className="text-xs text-slate-400">{v.visiting_for} · {v.qr_source}{v.lead_stage ? ` · ${v.lead_stage}` : ""}</p>
                  </div>
                  <a href={waLink(v.mobile, waMessage(v.full_name, v.visiting_for))} target="_blank" rel="noreferrer" className="text-[#25D366] p-2"><MessageCircle size={18} /></a>
                  <button onClick={() => exit(v.visit_id)} data-testid="mark-exit-btn" className="text-slate-400 hover:text-[#D95D1E] p-2" title="Log exit"><LogOut size={18} /></button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Real-time Visitor Feed">
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {live.feed.map((f) => (
              <div key={f.visit_id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/visitors/${f.visitor_id}`)}>
                <div className="font-mono-num text-xs text-slate-400 w-16">{f.time_label}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#081226] truncate">{f.full_name}</p>
                  <p className="text-xs text-slate-400">{f.visiting_for} · in {f.time_label}{f.exit_label ? ` · out ${f.exit_label}` : ""}</p>
                </div>
                {f.exit_reason === "Meeting done"
                  ? <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-teal-100 text-teal-700" data-testid="feed-meeting-done-tag">Meeting done · Outside</span>
                  : <StatusBadge status={f.inside ? "Inside" : (f.lead_stage || "Exited")} />}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
